/**
 * `compressed.json` (docs/WALK_FORMAT.md, "Compressed textures"; walk-bake spec section 8): ASTC
 * copies of the lightmaps and textures, which the viewer loads where the GPU decodes them. Walks
 * that keep their originals open everywhere. A walk exported with the originals dropped has no
 * fallback, so the Player refuses it on a device without ASTC HDR before anything is saved.
 */
import { readEntryJson, type ZipEntry } from "../zip/zip.ts";
import type { AstcSupport } from "./memory.ts";

export const NEEDS_ASTC_HDR_MESSAGE =
  "This walk was exported without its original textures and needs ASTC HDR, which this device's graphics do not support. Export it again keeping the originals.";

/** True only for a manifest that explicitly says `originals: false`. Anything else keeps the originals. */
export function dropsOriginals(manifest: unknown): boolean {
  return typeof manifest === "object" && manifest !== null && (manifest as { originals?: unknown }).originals === false;
}

/**
 * Throws NEEDS_ASTC_HDR_MESSAGE when the walk's `compressed.json` has `originals: false` and the
 * device has no ASTC HDR. A missing, unreadable or malformed manifest never blocks opening.
 */
export async function assertOriginalsOrAstc(
  blob: Blob,
  index: Map<string, ZipEntry>,
  root: string,
  support: AstcSupport,
): Promise<void> {
  if (support.hdr && support.ldr) return;
  const entry = index.get(`${root}compressed.json`);
  if (!entry) return;
  let manifest: unknown;
  try {
    manifest = await readEntryJson<unknown>(blob, entry);
  } catch {
    return;
  }
  if (dropsOriginals(manifest)) throw new Error(NEEDS_ASTC_HDR_MESSAGE);
}
